import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Save, X, AlertCircle, Package, DollarSign, Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardHeader, CardContent } from '@/components/Card';
import { Input } from '@/components/Input';
import { ProductSearch } from '@/components/ProductSearch';
import { providerProductPriceService, ProviderProductPrice } from '@/services/providerProductPriceService';
// ...existing code...
import { Product } from '@/types/product';
import { clsx } from 'clsx';

interface ProviderProductsListProps {
  providerId: number;
  onUpdate?: () => void;
}

interface ProductPriceRow extends ProviderProductPrice {
  product_name?: string;
  product_description?: string;
  isEditing?: boolean;
  isNew?: boolean;
  tempPrice?: string;
  tempSupplierCode?: string;
  hasError?: boolean;
  errorMessage?: string;
}

/**
 * Componente para gerenciar a lista de produtos de um fornecedor
 * com edição inline de preços e adição de novos produtos
 */
export const ProviderProductsList: React.FC<ProviderProductsListProps> = ({
  providerId,
  onUpdate
}) => {
  const [products, setProducts] = useState<ProductPriceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 20,
    total_records: 0,
    total_pages: 1
  });
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const addRowRef = useRef<HTMLDivElement>(null);

  // Carregar ao trocar de fornecedor ou de página
  useEffect(() => {
    loadProviderProducts();
  }, [providerId, pagination.current_page, sortBy, sortDirection]);

  // Busca com debounce de 500 ms (mesmo padrão da listagem de produtos)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.current_page !== 1) {
        setPagination(prev => ({ ...prev, current_page: 1 })); // vai acionar o effect acima
      } else {
        loadProviderProducts();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const loadProviderProducts = async () => {
    if (!providerId) return;
    
    console.debug('[ProviderProductsList] loadProviderProducts called for provider:', providerId);
    setLoading(true);
    try {
      const response = await providerProductPriceService.getProviderProductPrices(
        providerId,
        pagination.current_page,
        pagination.page_size,
        search.trim() || undefined,
        sortBy,
        sortDirection
      );

      console.debug('[ProviderProductsList] getProviderProductPrices response:', response);

      // Enriquecer cada item com a descrição real do produto (em paralelo)
      const productsWithDetails = response.product_prices.map((productPrice) => {
        return {
          ...productPrice,
          product_name: productPrice.product_name ?? productPrice.product_sku ?? `Produto #${productPrice.product_id}`,
          product_description: productPrice.product_description,
          isEditing: false,
          isNew: false
        };
      });

      setProducts(productsWithDetails);
      if (response.pagination) {
        setPagination(response.pagination);
      }
      setErrors({});
    } catch (error) {
      console.error('Erro ao carregar produtos do fornecedor:', error);
      setErrors({ general: error instanceof Error ? error.message : 'Erro ao carregar produtos' });
    } finally {
      setLoading(false);
    }
  };

  // Adicionar novo produto
  const handleAddProduct = async (product: Product) => {
    // Verificar se produto já está associado ao fornecedor
    const existingProduct = products.find(p => p.product_id === product.id);
    if (existingProduct) {
      setErrors({
        ...errors,
        [`product_${product.id}`]: 'Este produto já está associado a este fornecedor'
      });
      return;
    }

    const newRow: ProductPriceRow = {
      product_id: product.id,
      provider_id: providerId,
      price: 0,
      product_sku: product.sku,
      product_name: product.description,
      product_description: product.description,
      created_at: new Date().toISOString(),
      isEditing: true,
      isNew: true,
      tempPrice: '',
      tempSupplierCode: ''
    };

    setProducts(prev => [...prev, newRow]);
    setShowAddProduct(false);
    
    // Focus no campo de preço da nova linha
    setTimeout(() => {
      const priceInput = document.getElementById(`price-${product.id}`) as HTMLInputElement;
      if (priceInput) {
        priceInput.focus();
        priceInput.select();
      }
    }, 100);
  };

  // Iniciar edição de preço
  const startEditing = (productId: number, currentPrice: number) => {
    setProducts(prev => 
      prev.map(p => 
        p.product_id === productId 
          ? { ...p, isEditing: true, tempPrice: currentPrice.toString(), tempSupplierCode: p.supplier_code || '', hasError: false }
          : p
      )
    );
  };

  // Cancelar edição
  const cancelEditing = (productId: number, isNew: boolean) => {
    if (isNew) {
      setProducts(prev => prev.filter(p => p.product_id !== productId));
    } else {
      setProducts(prev => 
        prev.map(p => 
          p.product_id === productId 
            ? { ...p, isEditing: false, tempPrice: undefined, hasError: false }
            : p
        )
      );
    }
    
    // Limpar erro específico
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`product_${productId}`];
      return newErrors;
    });
  };

  // Salvar preço
  const savePrice = async (productId: number, isNew: boolean) => {
    const product = products.find(p => p.product_id === productId);
    if (!product || !product.tempPrice) return;

    const newPrice = parseFloat(product.tempPrice);
    const newSupplierCode = product.tempSupplierCode || undefined;
    
    // Validar preço
    if (isNaN(newPrice) || newPrice <= 0) {
      setProducts(prev => 
        prev.map(p => 
          p.product_id === productId 
            ? { ...p, hasError: true, errorMessage: 'Preço deve ser maior que zero' }
            : p
        )
      );
      return;
    }

    try {
      if (isNew) {
        // Criar novo preço
        await providerProductPriceService.createProviderProductPrice(
          productId,
          providerId,
          newPrice,
          newSupplierCode
        );
      } else {
        // Atualizar preço existente
        await providerProductPriceService.updateProviderProductPrice(
          productId,
          providerId,
          newPrice,
          newSupplierCode
        );
      }

      // Atualizar estado local
      setProducts(prev => 
        prev.map(p => 
          p.product_id === productId 
            ? { 
                ...p, 
                price: newPrice,
                supplier_code: newSupplierCode, 
                isEditing: false, 
                isNew: false, 
                tempPrice: undefined,
                tempSupplierCode: undefined,
                hasError: false,
                updated_at: new Date().toISOString() 
              }
            : p
        )
      );

      // Limpar erro específico
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`product_${productId}`];
        return newErrors;
      });

      onUpdate?.();
    } catch (error) {
      console.error('Erro ao salvar preço:', error);
      setProducts(prev => 
        prev.map(p => 
          p.product_id === productId 
            ? { 
                ...p, 
                hasError: true, 
                errorMessage: error instanceof Error ? error.message : 'Erro ao salvar preço' 
              }
            : p
        )
      );
    }
  };

  // Remover produto
  const removeProduct = async (productId: number, isNew: boolean) => {
    if (!confirm('Deseja realmente remover este produto?')) {
      return;
    }

    if (isNew) {
      // Remover linha nova sem fazer API call
      setProducts(prev => prev.filter(p => p.product_id !== productId));
      return;
    }

    try {
      await providerProductPriceService.deleteProviderProductPrice(productId, providerId);
      setProducts(prev => prev.filter(p => p.product_id !== productId));
      onUpdate?.();
    } catch (error) {
      console.error('Erro ao remover produto:', error);
      setErrors({
        ...errors,
        [`product_${productId}`]: 'Erro ao remover produto'
      });
    }
  };

  // Atualizar preço temporário
  const updateTempPrice = (productId: number, value: string) => {
    setProducts(prev => 
      prev.map(p => 
        p.product_id === productId 
          ? { ...p, tempPrice: value, hasError: false, errorMessage: undefined }
          : p
      )
    );
  };

  // Atualizar código do fornecedor temporário
  const updateTempSupplierCode = (productId: number, value: string) => {
    setProducts(prev => 
      prev.map(p => 
        p.product_id === productId 
          ? { ...p, tempSupplierCode: value }
          : p
      )
    );
  };

  // Handle key press para salvar com Enter
  const handleKeyPress = (e: React.KeyboardEvent, productId: number, isNew: boolean) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      savePrice(productId, isNew);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing(productId, isNew);
    }
  };

  // Obter IDs dos produtos já associados para exclusão na busca
  const excludeProductIds = products.map(p => p.product_id);

  // Toggle sort
  const toggleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const SortIcon: React.FC<{ field: string }> = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown size={14} className="text-slate-400" />;
    return sortDirection === 'asc' 
      ? <ArrowUp size={14} className="text-emerald-600" /> 
      : <ArrowDown size={14} className="text-emerald-600" />;
  };

  return (
    <Card>
      <CardHeader title="Produtos do Fornecedor" />
      <CardContent>
        {/* Barra superior: busca + botões */}
        <div className="mb-4 flex flex-col md:flex-row gap-3">
          {/* Campo de busca */}
          <div className="flex-1">
            <div className="relative">
              <Input
                placeholder="Buscar por nome ou SKU..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                startIcon={<Search size={18} />}
              />
              {search && loading && (
                <div className="absolute right-3 top-2.5">
                  <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              )}
            </div>
            {search && (
              <p className="text-xs text-slate-500 mt-1">
                Buscando por: <span className="font-medium">"{search}"</span>
                {!loading && (
                  <span> — {pagination.total_records} resultado{pagination.total_records !== 1 ? 's' : ''}</span>
                )}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {!search && pagination.total_records > 0 && (
              <span className="text-sm text-slate-500 whitespace-nowrap">
                {pagination.total_records} produto{pagination.total_records !== 1 ? 's' : ''}
              </span>
            )}
            {!showAddProduct && (
              <>
                <Button
                  onClick={() => setShowAddProduct(true)}
                  icon={<Plus size={16} />}
                  size="sm"
                  variant="outline"
                >
                  Adicionar Produto
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Campo para adicionar produto */}
        {showAddProduct && (
          <div ref={addRowRef} className="mb-4 p-4 border border-blue-200 rounded-lg bg-blue-50">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <ProductSearch
                  onProductSelect={handleAddProduct}
                  placeholder="Buscar produto por SKU ou nome para adicionar..."
                  excludeProductIds={excludeProductIds}
                />
              </div>
              <Button
                onClick={() => setShowAddProduct(false)}
                variant="outline"
                size="sm"
                icon={<X size={16} />}
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {/* Mensagem de erro geral */}
        {errors.general && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between space-x-2">
            <div className="flex items-center space-x-2">
              <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
              <span className="text-red-700">{errors.general}</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadProviderProducts}
              className="text-red-700 border-red-300 hover:bg-red-100 flex-shrink-0"
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {/* Tabela de produtos */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead className="bg-slate-50">
              <tr>
                <th className="border border-slate-200 px-4 py-3 text-left text-sm font-medium text-slate-700">
                  <button
                    type="button"
                    onClick={() => toggleSort('sku')}
                    className="flex items-center gap-1.5 hover:text-emerald-700 transition-colors"
                  >
                    <span>SKU</span>
                    <SortIcon field="sku" />
                  </button>
                </th>
                <th className="border border-slate-200 px-4 py-3 text-left text-sm font-medium text-slate-700">
                  <button
                    type="button"
                    onClick={() => toggleSort('description')}
                    className="flex items-center gap-1.5 hover:text-emerald-700 transition-colors"
                  >
                    <Package size={16} />
                    <span>Descrição</span>
                    <SortIcon field="description" />
                  </button>
                </th>
                <th className="border border-slate-200 px-4 py-3 text-left text-sm font-medium text-slate-700">Cód. Fornecedor</th>
                <th className="border border-slate-200 px-4 py-3 text-right text-sm font-medium text-slate-700">
                  <div className="flex items-center justify-end space-x-2">
                    <DollarSign size={16} />
                    <span>Preço</span>
                  </div>
                </th>
                <th className="border border-slate-200 px-4 py-3 text-right text-sm font-medium text-slate-700">Est. Físico</th>
                <th className="border border-slate-200 px-4 py-3 text-right text-sm font-medium text-slate-700">Est. Disponível</th>
                <th className="border border-slate-200 px-4 py-3 text-center text-sm font-medium text-slate-700">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && products.length === 0 ? (
                <tr>
                  <td colSpan={7} className="border border-slate-200 px-4 py-8 text-center text-slate-500">
                    Carregando produtos...
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={7} className="border border-slate-200 px-4 py-8 text-center text-slate-500">
                    Nenhum produto associado a este fornecedor.
                    <br />
                    <button
                      type="button"
                      onClick={() => setShowAddProduct(true)}
                      className="text-blue-600 hover:text-blue-800 mt-2 underline"
                    >
                      Adicionar o primeiro produto
                    </button>
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr 
                    key={product.product_id}
                    className={clsx({
                      'bg-blue-50': product.isNew,
                      'bg-red-50': product.hasError || errors[`product_${product.product_id}`]
                    })}
                  >
                    <td className="border border-slate-200 px-4 py-3">
                      <span className="font-mono text-sm text-slate-700">{product.product_sku || '—'}</span>
                    </td>
                    <td className="border border-slate-200 px-4 py-3">
                      <span className="font-medium text-slate-900">
                        {product.product_description || product.product_name || 'Nome não disponível'}
                      </span>
                    </td>
                    
                    <td className="border border-slate-200 px-4 py-3">
                      {product.isEditing ? (
                        <input
                          id={`supplier-code-${product.product_id}`}
                          type="text"
                          maxLength={100}
                          value={product.tempSupplierCode || ''}
                          onChange={(e) => updateTempSupplierCode(product.product_id, e.target.value)}
                          onKeyDown={(e) => handleKeyPress(e, product.product_id, product.isNew || false)}
                          className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                          placeholder="Cód. fornecedor"
                        />
                      ) : (
                        <div
                          className="cursor-pointer hover:bg-slate-100 px-2 py-1 rounded"
                          onClick={() => startEditing(product.product_id, product.price)}
                          title="Clique para editar"
                        >
                          <span className="text-sm text-slate-600">
                            {product.supplier_code || '—'}
                          </span>
                        </div>
                      )}
                    </td>
                    
                    <td className="border border-slate-200 px-4 py-3 text-right">
                      {product.isEditing ? (
                        <div className="flex flex-col items-end space-y-1">
                          <input
                            id={`price-${product.product_id}`}
                            type="number"
                            step="0.01"
                            min="0"
                            value={product.tempPrice || ''}
                            onChange={(e) => updateTempPrice(product.product_id, e.target.value)}
                            onKeyDown={(e) => handleKeyPress(e, product.product_id, product.isNew || false)}
                            className={clsx(
                              'w-24 px-2 py-1 text-right text-sm border rounded',
                              'focus:ring-2 focus:ring-blue-500',
                              {
                                'border-red-300 focus:ring-red-500': product.hasError
                              }
                            )}
                            placeholder="0.00"
                            autoFocus
                          />
                          {product.hasError && product.errorMessage && (
                            <div className="text-xs text-red-600">
                              {product.errorMessage}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div 
                          className="cursor-pointer hover:bg-slate-100 px-2 py-1 rounded"
                          onClick={() => startEditing(product.product_id, product.price)}
                          title="Clique para editar o preço"
                        >
                          <span className="font-mono">
                            R$ {product.price.toFixed(2)}
                          </span>
                        </div>
                      )}
                      
                      {/* Erro específico do produto */}
                      {errors[`product_${product.product_id}`] && (
                        <div className="text-xs text-red-600 mt-1">
                          {errors[`product_${product.product_id}`]}
                        </div>
                      )}
                    </td>

                    <td className="border border-slate-200 px-4 py-3 text-right">
                      <span className="text-sm text-slate-700">{product.stock_quantity ?? '—'}</span>
                    </td>
                    <td className="border border-slate-200 px-4 py-3 text-right">
                      <span className="text-sm text-slate-700">{product.stock_quantity_available ?? '—'}</span>
                    </td>
                    
                    <td className="border border-slate-200 px-4 py-3">
                      <div className="flex items-center justify-center space-x-1">
                        {product.isEditing ? (
                          <>
                            <Button
                              onClick={() => savePrice(product.product_id, product.isNew || false)}
                              size="sm"
                              variant="outline"
                              icon={<Save size={14} />}
                              className="text-green-600 hover:text-green-800"
                            >
                              Salvar
                            </Button>
                            <Button
                              onClick={() => cancelEditing(product.product_id, product.isNew || false)}
                              size="sm"
                              variant="outline"
                              icon={<X size={14} />}
                              className="text-slate-600 hover:text-slate-800"
                            >
                              Cancelar
                            </Button>
                          </>
                        ) : (
                          <Button
                            onClick={() => removeProduct(product.product_id, product.isNew || false)}
                            size="sm"
                            variant="outline"
                            icon={<Trash2 size={14} />}
                            className="text-red-600 hover:text-red-800"
                          >
                            Remover
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {pagination.total_pages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-slate-600">
              Página {pagination.current_page} de {pagination.total_pages}
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                disabled={pagination.current_page <= 1}
                size="sm"
                variant="outline"
              >
                Anterior
              </Button>
              
              <Button
                onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                disabled={pagination.current_page >= pagination.total_pages}
                size="sm"
                variant="outline"
              >
                Próximo
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};